import React from "react";
import { useState, useCallback, useEffect, useMemo } from "react";
import useAxiosClient from '../axiosClient';
import { Icon, Button, Frame, Toast, Banner, Text, Tooltip } from "@shopify/polaris";
// import { StatBox } from "../polaris-viz";
import { useTranslation } from 'react-i18next';
import { LocationIcon, StatusActiveIcon, WorkIcon, PersonIcon, ClockIcon, ExternalSmallIcon, DuplicateIcon, TeamIcon, CreditCardIcon } from "@shopify/polaris-icons";
import { Users, ChevronLeft, ChevronRight, BookOpen, Calendar, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { IconDisc, ColumnHeading, CardHeader, SURFACE, TINT, MetricCard, ProgressTrack } from '../ui/CardSurface';
import { buildPortalUrl } from '../../utils/portal';
import { DOCS_URL, openSupport } from '../../support';
import { WeekActivityChart, OnShiftPanel } from './DashboardPanels';

/** The same call the setup guide and the welcome screen book. */
const BOOK_A_CALL_URL = 'https://calendly.com/talk-to-specialist/30min';


export default function Dashboard() {
  const { t } = useTranslation();
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [user_id, setUser] = useState('');
  const [theme_id, setTheme] = useState('');
  const [shop_name, setShop] = useState('');
  const [Toaster, setToaster] = useState(false);
  const [errorBannerContent, setErrorBannerContent] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // The API caps recent_activity at ten rows, so five-at-a-time paged through
  // data the browser already had while the table ended half way down the page.
  const [pageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);

  // Dashboard stats with initial empty values to prevent rendering errors
  const [dashboardStats, setDashboardStats] = useState({
    total_staff: 0,
    active_staff: 0,
    // The plan's seat allowance: a number, or the string 'Unlimited'. Read by the
    // third stat card; null until the first response lands.
    staff_limit: null,
    recent_activity: [],
    // Added to /dashboard-stats alongside the panels that draw them. null,
    // not [], so neither panel renders an empty state in the moment before the
    // first response lands - and so a backend that predates these fields, which
    // sends neither, is treated the same way rather than as "nobody".
    week_activity: null,
    on_shift: null
  });

  // Helper functions
  const toggleToaster = useCallback(() => setToaster((Toaster) => !Toaster), []);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardStats = async () => {
      setIsLoading(true);
      try {
        // The browser's own date, for the seven-day window. clock_events
        // carries a per-punch timezone rather than a shop-level one, so the
        // server has no authoritative "today" for this merchant.
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const endDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

        const response = await axiosClient.get('/dashboard-stats', {
          params: { end_date: endDate },
        });
        if (response.data.success) {
          setUser(response.data.uid);
          setTheme(response.data.theme_id);
          // Prefer the API's shop domain; fall back to the user saved at login/signup.
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          setShop(response.data.shop || response.data.name || storedUser?.name || storedUser?.shop || '');
          setDashboardStats(response.data.stats);
          setCurrentPage(1);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Update paginated data when dashboard stats or page changes
  useEffect(() => {
    if (dashboardStats.recent_activity) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const slicedData = dashboardStats.recent_activity.slice(startIndex, endIndex);
      setPaginatedData(slicedData);
      setHasNextPage(endIndex < dashboardStats.recent_activity.length);
    }
  }, [currentPage, pageSize, dashboardStats.recent_activity]);

  const totalStaff = dashboardStats.total_staff || 0;
  const portalUrl = buildPortalUrl(shop_name);

  /**
   * Seat usage for the third stat card.
   *
   * paid_available_staffs is either a number or the literal string 'Unlimited' -
   * getStaff() compares against exactly that - so both shapes are handled. On an
   * account whose allowance we cannot read we say how many seats are in use and
   * stop there rather than invent a denominator.
   */
  // On the team but not clocked in. Both counts are already on this page, so
  // the badge cannot disagree with the two cards beside it.
  const offShiftCount = Math.max(0, totalStaff - (Number(dashboardStats.active_staff) || 0));

  const seats = useMemo(() => {
    const raw = dashboardStats.staff_limit;

    if (typeof raw === 'string' && raw.trim().toLowerCase() === 'unlimited') {
      return { value: totalStaff, caption: t('seats_unlimited_caption'), limit: null };
    }

    const limit = Number(raw);

    if (!Number.isFinite(limit) || limit <= 0) {
      return { value: totalStaff, caption: t('seats_in_use_caption'), limit: null };
    }

    const full = totalStaff >= limit;

    return {
      value: `${totalStaff} / ${limit}`,
      caption: full
        ? t('seats_all_in_use')
        : t('seats_available_count', { remaining: limit - totalStaff }),
      limit,
      full,
      // Named by the badge, so it is computed with the rest rather than
      // recalculated at the call site where it could drift.
      left: Math.max(0, limit - totalStaff),
      percent: Math.min(100, Math.round((totalStaff / limit) * 100)),
    };
  }, [dashboardStats.staff_limit, totalStaff, t]);

  // Toast markup
  const toastMarkup = Toaster ? (
    <Frame>
      <Toast content={t("settings_saved")} onDismiss={toggleToaster} />
    </Frame>
  ) : null;

  const activityCount = dashboardStats.recent_activity?.length || 0;
  const rangeStart = activityCount ? ((currentPage - 1) * pageSize) + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, activityCount);

  return (
    <div>
      {/* Title and wordmark on one line, as on the Staff page. The subtitle
          says what the page is; without it the heading sat alone above a row of
          bare numbers. */}
      {/* The wordmark used to sit on the right: the app's own mark, shown to
          someone already inside the app, in the one slot on the page where an
          action belongs - and AppFooter draws it on every page anyway. The
          primary action goes there instead. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Text as="h1" variant="headingLg">{t('overview')}</Text>
          <div style={{ marginTop: '1px' }}>
            <Text as="p" variant="bodySm" tone="subdued">{t('dashboard_subtitle')}</Text>
          </div>
        </div>
        <Button variant="primary" onClick={() => navigate('/StaffManagement')}>
          {t('add_staff')}
        </Button>
      </div>

      {errorBannerContent !== '' && (
        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
          <Banner
            tone="critical"
            onDismiss={() => setErrorBannerContent("")}
          >
            <Text variant="bodyMd">{errorBannerContent}</Text>
          </Banner>
        </div>
      )}

      {/* Three cards, one row, and the portal link is no longer a fourth of
          them - it is the banner below. Four cards meant the row reflowed to
          3 + 1 between 1100 and 1390px, so the dark card sat alone across the
          full width at laptop widths and beside the others only on a desktop.

          MetricCard rather than a local StatCard: the badge carries a DERIVED
          fact - "3 off shift", "1 still available" - which is something a
          merchant cannot read off the number beside it. Both are computed from
          counts already on this page, so they add no request and cannot
          disagree with the values above them. */}
      <div className="metric-grid mb-4">
        <MetricCard
          icon={TeamIcon}
          label={t("total_staff")}
          value={totalStaff}
          tooltip={t("stat_total_staff_caption")}
          badge={offShiftCount > 0 ? t("stat_off_shift", { count: offShiftCount }) : null}
        />
        <MetricCard
          icon={ClockIcon}
          label={t("active_staff")}
          value={dashboardStats.active_staff}
          tooltip={t("stat_clocked_in_caption")}
        />
        <MetricCard
          icon={CreditCardIcon}
          label={t("seats_used")}
          value={seats.value}
          badge={
            seats.limit
              ? (seats.full
                  // At the ceiling the badge stops describing and starts
                  // acting: "Limit reached" only repeated what the 1 / 1 and
                  // the full bar already said, and left nowhere to go. It
                  // survives as the button's accessible name.
                  ? t("upgrade_plan")
                  : t("seats_still_available", { count: seats.left }))
              : null
          }
          // Warning, not critical. Being at the seat ceiling is a state to act
          // on, not an error, and it is keyed to the SAME 80% threshold as the
          // bar below it - otherwise the bar turned amber while the badge was
          // still blue, two readings of one number on one card.
          badgeTone={seats.limit && seats.percent >= 80 ? "warning" : undefined}
          badgeTitle={seats.full ? t("seat_limit_reached") : undefined}
          badgeOnClick={seats.full ? () => navigate("/Pricing") : undefined}
          footer={seats.limit ? <ProgressTrack percent={seats.percent} /> : null}
        />
      </div>

      {/* The staff portal link, full width under the metrics - the same banner
          the Shopify app draws. It is the one bold surface on the page, and at
          full width the address fits without truncating mid-domain, which is
          what it did at a quarter of the row. */}
      <div className="portal-banner mb-4">
        <div className="portal-banner__intro">
          <span className="portal-banner__disc">
            <Users style={{ width: '26px', height: '26px' }} strokeWidth={2} />
          </span>

          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="portal-banner__tag">{t("new_feature")}</span>
              <span style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.2px' }}>
                {t("staff_portal_features")}
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
              {t("your_staff_members_can_now_clock_in_out")}
            </p>
          </div>
        </div>

        <div className="portal-banner__link">
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '6px',
            }}
          >
            {t("portal_link")}
          </div>

          <div className="portal-banner__field">
            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontSize: '13px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: portalUrl ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.45)',
              }}
            >
              {portalUrl || t("loading")}
            </div>

            <Tooltip content={t("copy_link")}>
              <button
                type="button"
                className="portal-link-action"
                style={{ width: '30px', height: '30px' }}
                disabled={!portalUrl}
                aria-label={t("copy_link")}
                onClick={() => {
                  navigator.clipboard.writeText(portalUrl);
                  if (window.shopify?.toast?.show) window.shopify.toast.show(t("link_copied"));
                }}
              >
                <span style={{ display: 'inline-flex', width: '17px', height: '17px' }}>
                  <Icon source={DuplicateIcon} tone="inherit" />
                </span>
              </button>
            </Tooltip>

            <Tooltip content={t("open_portal")}>
              <button
                type="button"
                className="portal-link-action"
                style={{ width: '30px', height: '30px' }}
                disabled={!portalUrl}
                aria-label={t("open_portal")}
                onClick={() => window.open(portalUrl, '_blank', 'noopener,noreferrer')}
              >
                <span style={{ display: 'inline-flex', width: '17px', height: '17px' }}>
                  <Icon source={ExternalSmallIcon} tone="inherit" />
                </span>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* The chart and the on-shift list. Both return null when the API has
          not sent their data, so this renders exactly as before against a
          backend that has not been deployed yet. */}
      {(Array.isArray(dashboardStats.week_activity) || Array.isArray(dashboardStats.on_shift)) && (
        <div className="dash-split mb-4">
          <WeekActivityChart data={dashboardStats.week_activity} />
          <OnShiftPanel
            people={dashboardStats.on_shift}
            count={dashboardStats.active_staff}
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* The shared card header the rest of the app uses, rather than this
            page's own <h3>. */}
        {/* The range and the pager used to be a separate 65px band under the
            table that could never paginate past one extra page. Here the count
            replaces a Badge that was showing it decoratively, and the chevrons
            only exist when there is somewhere to go. */}
        <CardHeader
          icon={ClockIcon}
          tone="info"
          title={t("recent_activity")}
          context={activityCount > pageSize
            ? `${rangeStart}-${rangeEnd} ${t("of")} ${activityCount}`
            : `${activityCount} ${t("total")}`}
          actions={activityCount > pageSize ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-gray-600"
                aria-label={t("previous")}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!hasNextPage}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-gray-600"
                aria-label={t("next")}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            {/* Five labelled columns over an empty table is scaffolding for data
                that is not there - 41px of it. */}
            {paginatedData.length > 0 && (
            <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-3 text-[13px] font-medium text-[#616161]"><ColumnHeading icon={PersonIcon}>{t("name")}</ColumnHeading></th>
                  <th className="px-6 py-3 text-[13px] font-medium text-[#616161]"><ColumnHeading icon={LocationIcon}>{t("location")}</ColumnHeading></th>
                  <th className="px-6 py-3 text-[13px] font-medium text-[#616161]"><ColumnHeading icon={StatusActiveIcon}>{t("status")}</ColumnHeading></th>
                  <th className="px-6 py-3 text-[13px] font-medium text-[#616161]"><ColumnHeading icon={ClockIcon}>{t("recent_activity")}</ColumnHeading></th>
                  <th className="px-6 py-3 text-[13px] font-medium text-[#616161]"><ColumnHeading icon={WorkIcon}>{t("position")}</ColumnHeading></th>
                </tr>
              </thead>
            )}
            <tbody className="divide-y divide-gray-100 bg-white">
              {paginatedData.length > 0 ? (
                paginatedData.map((activity, index) => (
                  <tr key={index} className="hover:bg-gray-50/80 transition-colors duration-150 group">
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 text-sm rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold border border-gray-200 group-hover:border-gray-300 transition-colors">
                          {getInitials(activity.first_name, activity.last_name)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900">
                            {`${activity.first_name} ${activity.last_name}`}
                          </span>
                          {activity.email && (
                            <span className="text-xs text-gray-500">{activity.email}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                      {activity.location_name}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${activity.event_type === 'clock-in'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${activity.event_type === 'clock-in' ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                        {activity.event_type === 'clock-in' ? t("clocked_in") : t("clocked_out")}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {new Date(activity.activity_time).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                      {activity.position}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-7 text-center">
                    {/* An empty table of zeroes gives a new user nothing to do.
                        Name the next step, and which one depends on whether they
                        have any staff yet. */}
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="bg-gray-100 p-3 rounded-full">
                        <Icon source={ClockIcon} tone="subdued" />
                      </div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {(dashboardStats.total_staff || 0) === 0
                          ? t("dashboard_empty_no_staff_title")
                          : t("dashboard_empty_no_activity_title")}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {(dashboardStats.total_staff || 0) === 0
                          ? t("dashboard_empty_no_staff_body")
                          : t("dashboard_empty_no_activity_body")}
                      </Text>
                      <div className="mt-2">
                        <Button
                          variant="primary"
                          onClick={() => navigate((dashboardStats.total_staff || 0) === 0 ? '/StaffManagement' : '/Settings')}
                        >
                          {(dashboardStats.total_staff || 0) === 0
                            ? t("dashboard_empty_no_staff_cta")
                            : t("dashboard_empty_no_activity_cta")}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>


      </div>

      {/* Documentation, a call, and chat - the three ways out of a stuck
          setup, at the point where a merchant who has run out of ideas is
          looking. The sidebar carries two of them as links; a link in a rail is
          not the same as an offer of help on the page you are stuck on. */}
      <div className="support-grid">
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="support-card"
        >
          <span className="support-card__icon">
            <BookOpen style={{ width: '22px', height: '22px' }} strokeWidth={2} />
          </span>
          <h3 className="support-card__title">{t("documentation")}</h3>
          <p className="support-card__body">{t("comprehensive_guides_to_help_you_setup")}</p>
          <span className="support-card__cta">
            {t("read_guides")}
            <span aria-hidden="true">{'\u2192'}</span>
          </span>
        </a>

        <button type="button" className="support-card" onClick={() => window.open(BOOK_A_CALL_URL, '_blank', 'noopener,noreferrer')}>
          <span className="support-card__icon">
            <Calendar style={{ width: '22px', height: '22px' }} strokeWidth={2} />
          </span>
          <h3 className="support-card__title">{t("book_consultation")}</h3>
          <p className="support-card__body">{t("schedule_one_on_one_meeting_with_expert")}</p>
          <span className="support-card__cta">
            {t("book_now")}
            <span aria-hidden="true">{'\u2192'}</span>
          </span>
        </button>

        {/* openSupport, not a hardcoded Crisp call: this build decides for
            itself whether chat is available and falls back to mail, so the card
            cannot offer a chat window that never opens. */}
        <button type="button" className="support-card" onClick={() => openSupport()}>
          <span className="support-card__icon">
            <MessageCircle style={{ width: '22px', height: '22px' }} strokeWidth={2} />
          </span>
          <h3 className="support-card__title">{t("live_support")}</h3>
          <p className="support-card__body">{t("chat_instantly_with_support_team")}</p>
          <span className="support-card__cta">
            {t("start_chat")}
            <span aria-hidden="true">{'\u2192'}</span>
          </span>
        </button>
      </div>

    </div>
  );
}